import React, { useCallback, useImperativeHandle, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useWatch } from "react-hook-form";
import CardTemplate from "components/card/card-template/CardTemplate";
import { HeightField, WeightField, HealthQuestionField } from "components/form-fields";
import { useQuote } from "hooks/useQuote";
import { useContactInformation } from "hooks/useContactInformation";
import type { DTOApplicationFormReturn, StatementOfHealthRef } from "types/Card";
import { StatementOfHealthStateSchema } from "components/card/statement-of-health/StatementOfHealthStateSchema";
import { getHealthQuestions } from "constants/statementOfHealthQuestions";
import {
    restoreStatementOfHealthData,
    extractStatementOfHealthValues,
} from "components/card/statement-of-health/statementOfHealthHelpers";
import { ROUTES } from "routes/constants";

interface StatementOfHealthProps {
    className?: string;
    form: DTOApplicationFormReturn;
}

type StatementOfHealthWithRef = StatementOfHealthProps & {
    ref?: React.Ref<StatementOfHealthRef>;
};

const StatementOfHealth = ({ ref, ...props }: StatementOfHealthWithRef) => {
    const { className = "", form } = props;
    // ========== Hooks ==========
    const navigate = useNavigate();
    const { quoteData } = useQuote();
    const { contactData } = useContactInformation();
    const {
        register,
        formState: { errors },
        setValue,
        trigger,
    } = form;

    // ========== State ==========
    const statementOfHealthDataRedux = contactData?.statementOfHealth;

    const statementOfHealth = useWatch({
        control: form.control,
        name: "statementOfHealth",
    });

    // ========== Computed Values ==========
    const applicationState = useMemo(
        () => ({
            application: quoteData
                ? {
                      state: quoteData.state,
                      hasUsedNicotineLastYear: quoteData.hasUsedNicotineLastYear,
                  }
                : undefined,
        }),
        [quoteData]
    );

    const parseResult = useMemo(() => StatementOfHealthStateSchema.safeParse(applicationState), [applicationState]);
    const validatedState = parseResult.success ? parseResult.data : null;
    const currentState = validatedState?.application?.state || "";
    const hasUsedNicotineLastYear = validatedState?.application?.hasUsedNicotineLastYear || false;
    const questions = useMemo(() => getHealthQuestions(currentState), [currentState]);

    // ========== Effects ==========
    // Validate state
    useEffect(() => {
        if (!parseResult.success) {
            console.error("StatementOfHealth state validation error:", parseResult.error);
            navigate(ROUTES.SOMETHING_WENT_WRONG);
        }
    }, [parseResult.success, parseResult.error, navigate]);

    // Restore form data from quote
    useEffect(() => {
        if (parseResult.success && parseResult.data.application) {
            const nicotineValue = parseResult.data.application.hasUsedNicotineLastYear || false;
            setValue("statementOfHealth.hasUsedNicotine", nicotineValue);
        }
    }, [parseResult.success, parseResult.data, setValue]);

    // Restore form data from Redux
    useEffect(() => {
        restoreStatementOfHealthData(statementOfHealthDataRedux, setValue);
    }, [statementOfHealthDataRedux, setValue]);

    // ========== Event Handlers ==========
    const handleRadioChange = useCallback(
        (field: "hasMedicalConditions" | "hasDiagnosticTesting", value: boolean) => {
            setValue(`statementOfHealth.${field}`, value);
            trigger(`statementOfHealth.${field}`);
        },
        [setValue, trigger]
    );

    // ========== Ref Methods ==========
    useImperativeHandle(
        ref,
        () => ({
            validate: () => trigger("statementOfHealth"),
            getValues: () => {
                const formData = form.getValues("statementOfHealth");
                return extractStatementOfHealthValues(formData, hasUsedNicotineLastYear);
            },
        }),
        [trigger, form, hasUsedNicotineLastYear]
    );

    // ========== Render ==========
    return (
        <CardTemplate title="Statement of Health" className={className}>
            <form className="flex flex-col gap-4" data-testid="statement-of-health-form" onSubmit={(e) => e.preventDefault()}>
                <div className="flex gap-4">
                    <HeightField
                        registerFeet={register("statementOfHealth.height.feet")}
                        registerInches={register("statementOfHealth.height.inches")}
                        errors={errors.statementOfHealth?.height?.feet || errors.statementOfHealth?.height?.inches}
                        feetId="statement-of-health-height-feet"
                        inchesId="statement-of-health-height-inches"
                    />
                    <WeightField
                        register={register("statementOfHealth.weight")}
                        error={errors.statementOfHealth?.weight?.message}
                        id="statement-of-health-weight"
                    />
                </div>

                <HealthQuestionField
                    question={questions.nicotine}
                    name="hasUsedNicotineLastYear"
                    value={hasUsedNicotineLastYear}
                    yesId="statement-of-health-has-used-nicotine-yes"
                    noId="statement-of-health-has-used-nicotine-no"
                    showEdit={true}
                    editRoute="/quote"
                />

                <HealthQuestionField
                    question={questions.medicalConditions}
                    name="hasMedicalConditions"
                    value={statementOfHealth?.hasMedicalConditions}
                    onChange={(value) => handleRadioChange("hasMedicalConditions", value)}
                    error={errors.statementOfHealth?.hasMedicalConditions?.message}
                    yesId="statement-of-health-has-medical-conditions-yes"
                    noId="statement-of-health-has-medical-conditions-no"
                />

                <HealthQuestionField
                    question={questions.diagnosticTesting}
                    name="hasDiagnosticTesting"
                    value={statementOfHealth?.hasDiagnosticTesting}
                    onChange={(value) => handleRadioChange("hasDiagnosticTesting", value)}
                    error={errors.statementOfHealth?.hasDiagnosticTesting?.message}
                    yesId="statement-of-health-has-diagnostic-testing-yes"
                    noId="statement-of-health-has-diagnostic-testing-no"
                />
            </form>
        </CardTemplate>
    );
};

StatementOfHealth.displayName = "StatementOfHealth";

export default StatementOfHealth;
